import { useState } from "react";
import { login } from "../lib/auth";
import { resolvePostLoginTarget } from "../lib/post-login";
import { Button } from "../components/ui/Button";

/**
 * Email + password sign-in (PR44). The optional slug input only appears after the
 * server returns `slug_required` (same email across multiple tenants).
 *
 * Honors a `?redirect=` (or legacy `?from=`) hash query param so a guard that
 * bounced the user here (RequireAdmin / RequireSignedIn) can send them back to
 * the page they wanted after a successful sign-in.
 *
 * Where they actually land is decided by resolvePostLoginTarget, NOT by
 * following the parameter blindly: a non-admin sent back to an admin-only page
 * is bounced to login again by the very guard that sent them here, forever.
 * See lib/post-login.ts.
 */
function parseReturnTo(): string | null {
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return null;
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  return params.get("redirect") || params.get("from");
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [slug, setSlug] = useState("");
  const [needSlug, setNeedSlug] = useState(false);
  const [tenants, setTenants] = useState<{ slug: string; display_name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await login(email, password, needSlug ? slug : undefined);
      if (result.ok) {
        const target = resolvePostLoginTarget({
          returnTo: parseReturnTo(),
          role: result.role,
          slug: result.tenant.slug,
        });
        // Carried across the navigation so the destination page can explain
        // itself; landing somewhere you did not click, with no word about it,
        // is its own small mystery.
        if (target.note) {
          try {
            sessionStorage.setItem("storesync:post-login-note", target.note);
          } catch {
            /* private mode — the redirect still works, only the note is lost */
          }
        }
        window.location.hash = target.hash;
        return;
      }
      if (result.error === "slug_required") {
        setNeedSlug(true);
        setTenants(result.tenants || []);
        if (result.tenants && result.tenants.length > 0) setSlug(result.tenants[0].slug);
        setError("This email is registered to more than one brand. Choose which one to sign in to.");
        return;
      }
      setError("Incorrect email or password.");
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm max-w-md mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Sign in to StoreSync</h2>
      <p className="text-sm text-gray-600 mb-5">Enter your email and password.</p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          autoFocus
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@brand.com"
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
        />
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
        />
        {needSlug && (
          tenants.length > 0 ? (
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
            >
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.display_name} ({t.slug})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Brand slug"
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 font-mono"
            />
          )
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        <Button type="submit" variant="primary" disabled={submitting} className="w-full">
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="mt-4 text-sm text-center">
        <a href="#/forgot-password" className="text-accent-700 hover:underline">
          Forgot password?
        </a>
      </div>
    </div>
  );
}
